/**
 * The job list and the job page from the offline pack (FM Technician B18 /
 * D8; R2 integration). services/offlineSync saves GET /tech/offline-pack per
 * person — my own open jobs with checklists, and a summary of Take job — and
 * useOfflineData reads it. When GET /tech/jobs or /tech/jobs/{kind}/{id}
 * fails for want of a signal, the page shows these rows instead, marked
 * "data from HH:MM".
 *
 * What the pack does not carry is left empty rather than guessed: no SLA time
 * left, no due time, no zone, no AMC flag, nothing for "Done this shift" (it
 * is not saved), no actions (they need the server's answer).
 */
import type { JobFilter, JobPage, JobRow, JobTab } from '@/components/Tech/techApi';
import type { PackAsset, PackJob, PackTake } from '@/services/offlineSync';
import { holdReasonLabel } from '@/utils/workOrderHold';

/** True when a read failed for want of a network (no HTTP answer at all). */
export function noSignal(error: unknown): boolean {
  return !(error as { response?: unknown } | null)?.response;
}

function words(status: string): string {
  const s = status.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

type Priority = JobRow['priority'];
const PRIORITIES = ['critical', 'high', 'medium', 'low'];
function priorityOf(p: string | null | undefined): Priority {
  return p && PRIORITIES.includes(p) ? (p as Priority) : null;
}

function assetOf(id: string | null | undefined, assets: PackAsset[] | undefined): JobRow['asset'] {
  if (!id) return null;
  const a = (assets ?? []).find((x) => x.id === id);
  return a ? { id: a.id, name: a.name, code: a.code } : null;
}

export function rowFromPackJob(j: PackJob, assets?: PackAsset[]): JobRow {
  return {
    kind: j.kind, id: j.id, ref: j.ref, title: j.title,
    site: j.site_name, site_id: j.building_id, site_timezone: null,
    asset: assetOf(j.asset_id, assets),
    priority: priorityOf(j.priority),
    status: j.status, status_label: words(j.status),
    wo_type: j.kind === 'wo' ? (j.wo_type ?? null) : null,
    amc: false, due_at: null, sla_left_min: null,
    // Guide walk: the reason in words, as online ("Held while you ran an
    // emergency"), never "On hold: On hold".
    hold: j.held ? { reason: j.hold_reason ?? '', label: j.hold_label || holdReasonLabel(j.hold_reason),
      since: null, note: null } : null,
    running: !!j.started_at && !j.held && j.status === 'in_progress',
    accepted_at: null,
  };
}

export function rowFromPackTake(t: PackTake): JobRow {
  return {
    kind: t.kind, id: t.id, ref: t.ref, title: t.title,
    site: t.site_name, site_id: null, site_timezone: null, asset: null,
    priority: priorityOf(t.priority), status: 'open', status_label: 'Open',
    wo_type: null, amc: false, due_at: null, sla_left_min: null, hold: null, running: false, accepted_at: null,
  };
}

/** The filter chips, as far as the saved rows can answer them. */
function passes(row: JobRow, filter: JobFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'ticket' || filter === 'wo' || filter === 'inspection') return row.kind === filter;
  if (filter === 'ppm') return row.kind === 'wo' && row.wo_type === 'preventive';
  return false;      // AMC: the pack does not mark AMC visits
}

/** The saved rows for a tab, or null when the phone holds nothing for it. */
export function offlineRows(tab: JobTab, filter: JobFilter,
  saved: { jobs?: PackJob[]; take?: PackTake[]; assets?: PackAsset[] }): JobRow[] | null {
  if (tab === 'mine' && saved.jobs) {
    const rows = saved.jobs.map((j) => rowFromPackJob(j, saved.assets)).filter((r) => passes(r, filter));
    return rows.sort((a, b) => Number(b.running) - Number(a.running));
  }
  if (tab === 'take' && saved.take) return saved.take.map(rowFromPackTake).filter((r) => passes(r, filter));
  return null;
}

/** The job page's header from the pack (no actions, no history). */
export function pageFromPack(kind: string | undefined, id: string | undefined, jobs: PackJob[] | undefined,
  assets?: PackAsset[]): (JobPage & { checklist: unknown[] }) | null {
  const j = (jobs ?? []).find((x) => x.kind === kind && x.id === id);
  if (!j) return null;
  return {
    ...rowFromPackJob(j, assets),
    description: j.description ?? null, location_detail: j.location_detail ?? null,
    started_at: j.started_at ?? null, banner: null, actions: {}, history: [], checklist: j.checklist ?? [],
  };
}

/** One saved checklist line: its words and whether it is done. */
export function checklistLine(item: unknown): { text: string; done: boolean } {
  const o = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
  const text = [o.description, o.title, o.item, o.label, o.name, o.text]
    .find((v) => typeof v === 'string' && v.trim()) as string | undefined;
  const status = typeof o.status === 'string' ? o.status : '';
  return { text: text ?? 'Step', done: o.is_completed === true || o.completed === true || ['pass', 'done', 'completed'].includes(status) };
}
